import { useEffect, useRef, useState } from "react";
import { useGpsPings, useMCPTT, useNetwork } from "../mcptt/hooks";

interface Props {
  open:           boolean;
  onClose:        () => void;
  networkId:      string | null;
  /** Number of pings the GpsPingsDrawer thinks it has — passed in so
   *  the diagnostics panel can show the same number that's visible on
   *  the GPS chip. */
  gpsPingCount:   number;
}

/**
 * Live diagnostics overlay (bottom-right, toggleable). Designed to
 * make the silent-audio / blank-map / missing-GPS cases obvious to a
 * non-developer operator without opening DevTools.
 *
 * Sections:
 *  - Connection: WS state, server welcome, codec config from SERVER_CONFIG
 *  - Audio: RX/TX packet counters with bytes/sec since last poll,
 *           plus a heuristic "silent calls" warning
 *  - Account: dispatcher login / current network / group scope
 *  - GPS: ping count + last ping age
 *
 * Polled at 1 Hz; cheap.
 */
export function DiagnosticsPanel({ open, onClose, networkId, gpsPingCount }: Props) {
  const { client, snapshot } = useMCPTT();
  const network = useNetwork(networkId);
  const gpsPings = useGpsPings(networkId);

  // 1Hz tick that triggers re-renders for the live counters.
  const [, force] = useState(0);
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [open]);

  // Track delta since last render for "X B/s" rates. Stored in a ref
  // so re-renders don't churn the comparison.
  const prevRef = useRef({ rx: 0, tx: 0, ts: Date.now() });

  if (!open) return null;

  const stats = client.getStats();
  const cfg   = client.audioConfig;

  const now = Date.now();
  const dt  = Math.max(1, now - prevRef.current.ts);
  const rxBps = ((stats.voiceRxBytes - prevRef.current.rx) * 1000) / dt;
  const txBps = ((stats.voiceTxBytes - prevRef.current.tx) * 1000) / dt;
  prevRef.current = { rx: stats.voiceRxBytes, tx: stats.voiceTxBytes, ts: now };

  // Scope / tuning diagnostic: FleetConsole proves AllGroups:false
  // accounts can receive group audio, but only after the dispatcher
  // has PTT_RESPONSE-ACKed and "tuned" to the group with a short
  // PTT_REQUEST start/stop pair. We infer remaining failures from
  // PTT activity vs received voice.
  const sawPress  = stats.lastPressedTs > 0;
  const sawAnyRx  = stats.voiceRxCount  > 0;
  const recentSilentCalls = stats.silentCalls > 0;

  return (
    <div className="diag-panel" role="dialog" aria-label="Diagnostics">
      <div className="diag-head">
        <span className="diag-title">DIAGNOSTICS</span>
        <button type="button" className="diag-close" onClick={onClose} aria-label="Close diagnostics">{"\u2715"}</button>
      </div>

      <div className="diag-body">
        <DiagSection title="Connection">
          <DiagRow label="State"        value={snapshot.state} tone={snapshot.state === "ready" ? "ok" : "warn"} />
          <DiagRow label="User"         value={snapshot.userId ?? "(none)"} />
          <DiagRow label="Network"      value={network?.Name ?? "(none)"} />
          <DiagRow label="Audio state"  value={snapshot.audioState} tone={snapshot.audioState === "ready" ? "ok" : snapshot.audioState === "off" ? "muted" : "warn"} />
        </DiagSection>

        <DiagSection title="Server Audio Config (SERVER_CONFIG)">
          <DiagRow label="Sample rate"  value={cfg.sampleRate ? `${cfg.sampleRate} Hz` : "—"} />
          <DiagRow label="Bit rate"     value={cfg.bitRate ? `${cfg.bitRate} bps` : "—"} />
          <DiagRow label="Frame size"   value={cfg.frameSizeMs ? `${cfg.frameSizeMs} (samples or ms)` : "—"} />
          <DiagRow label="VoIP port"    value={cfg.voipPort === 0 ? "0 (voice over WSS)" : String(cfg.voipPort)} />
          <DiagRow label="Codec id"     value={String(cfg.codec)} />
          <DiagBanner tone="warn">
            Fleet Server reports bit rate separately from the actual WSS payload
            cadence; do not infer Opus from this value. Captured legacy
            VOICE_PACKETs are G.711-style bytes with A-law silence (`0xD5`).
            Echo911 defaults to A-law; switch to µ-law only if audio is still wrong.
          </DiagBanner>
        </DiagSection>

        <DiagSection title="Voice Frames (live)">
          <DiagRow label="RX packets"   value={String(stats.voiceRxCount)} tone={stats.voiceRxCount > 0 ? "ok" : "muted"} />
          <DiagRow label="RX bytes"     value={`${stats.voiceRxBytes.toLocaleString()} (${Math.round(rxBps)} B/s)`} />
          <DiagRow label="RX last seen" value={stats.voiceRxLastTs ? `${Math.round((now - stats.voiceRxLastTs) / 1000)}s ago` : "—"} />
          <DiagRow label="TX packets"   value={String(stats.voiceTxCount)} />
          <DiagRow label="TX bytes"     value={`${stats.voiceTxBytes.toLocaleString()} (${Math.round(txBps)} B/s)`} />
          <DiagRow label="TX last sent" value={stats.voiceTxLastTs ? `${Math.round((now - stats.voiceTxLastTs) / 1000)}s ago` : "—"} />
          <DiagRow label="Tuned groups" value={String(stats.tunedGroups ?? 0)} tone={(stats.tunedGroups ?? 0) > 0 ? "ok" : "muted"} />
          <DiagRow label="Muted targets" value={String(stats.mutedTargets ?? 0)} tone={(stats.mutedTargets ?? 0) > 0 ? "warn" : "muted"} />
          <DiagRow label="Silent calls" value={String(stats.silentCalls)} tone={stats.silentCalls > 0 ? "warn" : "muted"} />
          {sawPress && !sawAnyRx ? (
            <DiagBanner tone="danger">
              <strong>NO VOICE FRAMES RECEIVED.</strong> Radios have transmitted
              ({stats.silentCalls || 1} press{stats.silentCalls === 1 ? "" : "es"}) but the manager
              has not delivered a single VOICE_PACKET to this dispatcher.
              <br /><br />
              Select the talkgroup tile once so the v2 client can tune into
              the group&apos;s voice fan-out. If tuned groups is still 0, the
              silent start/stop registration failed.
            </DiagBanner>
          ) : recentSilentCalls ? (
            <DiagBanner tone="warn">
              {stats.silentCalls} press{stats.silentCalls === 1 ? "" : "es"} ended with zero voice packets received.
              Tune the selected group once, then test again.
            </DiagBanner>
          ) : null}
        </DiagSection>

        <DiagSection title="GPS">
          <DiagRow label="Pings (drawer)" value={String(gpsPingCount)} tone={gpsPingCount > 0 ? "ok" : "muted"} />
          <DiagRow label="Pings (hook)"   value={String(gpsPings.length)} />
          <DiagRow label="Last ping"      value={gpsPings[0] ? `${Math.round((now - gpsPings[0].ts) / 1000)}s ago` : "—"} />
          {gpsPingCount === 0 && gpsPings.length > 0 ? (
            <DiagBanner tone="warn">
              The hook sees pings but the drawer count says 0. State propagation
              issue at the Shell level. (Reload usually clears it.)
            </DiagBanner>
          ) : null}
        </DiagSection>

        <DiagSection title="Help">
          <div className="diag-help">
            Toggle this panel with the <strong>DIAG</strong> chip in the app bar
            or the keyboard shortcut <kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>D</kbd>.
            Counters update once per second.
          </div>
        </DiagSection>
      </div>
    </div>
  );
}

function DiagSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="diag-section">
      <div className="diag-section-head">{title}</div>
      <div className="diag-section-body">{children}</div>
    </div>
  );
}

function DiagRow({ label, value, tone }: { label: string; value: string; tone?: "ok" | "warn" | "danger" | "muted" }) {
  return (
    <div className="diag-row">
      <span className="diag-row-label">{label}</span>
      <span className={`diag-row-value ${tone ? `tone-${tone}` : ""}`}>{value}</span>
    </div>
  );
}

function DiagBanner({ tone, children }: { tone: "warn" | "danger"; children: React.ReactNode }) {
  return <div className={`diag-banner tone-${tone}`}>{children}</div>;
}
